import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Headphones, Phone, Database, ArrowRightLeft, Cloud, GitBranch, CheckCircle2, Shield, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Print all environment variables
console.log('Complete process.env:', process.env);

console.log('ProductDetailPage - API Config:', {
  BACKEND_URL,
  API
});

const iconMap = {
  Headphones,
  Phone,
  Database,
  ArrowRightLeft,
  Cloud,
  GitBranch
};

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      console.log('Fetching product:', productId);
      console.log('Request URL:', `${API}/products/${productId}`);
      
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Product Not Found</h2>
          <Link to="/products">
            <Button>View All Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = iconMap[product.icon] || Database;
  const pricingTiers = Object.entries(product.pricing).map(([tier, details]) => ({
    tier,
    ...details
  }));

  return (
    <div className="min-h-screen bg-white">
      {product && (
        <Helmet>
          <title>{product.name} | Digian</title>
          <meta name="description" content={product.description || `Learn about ${product.name} — an enterprise AI automation solution from Digian.`} />
          <meta property="og:title" content={`${product.name} | Digian`} />
          <meta property="og:description" content={product.description || `Learn about ${product.name} from Digian.`} />
          <meta property="og:url" content={`https://digian.ai/products/${product.id}`} />
          <link rel="canonical" href={`https://digian.ai/products/${product.id}`} />
        </Helmet>
      )}
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start space-x-6"
          >
            <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center flex-shrink-0">
              <Icon className="w-10 h-10 text-white" />
            </div>
            <div className="space-y-4">
              <div className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
                {product.category}
              </div>
              <h1 className="text-5xl font-bold text-slate-900" data-testid="product-detail-heading">
                {product.name}
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl">{product.description}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Overview */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={require('../assets/digian-wave.png')}
                alt="Digian.ai data wave visualization"
                className="rounded-2xl shadow-2xl w-full h-full object-contain bg-slate-950/80 p-2"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold text-slate-900">Key Features</h2>
              <div className="space-y-4">
                {product.features.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3" data-testid={`feature-${index}`}>
                    <CheckCircle2 className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Architecture/Benefits */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Enterprise-Grade Solution</h2>
            <p className="text-lg text-slate-600">Built for security, scalability, and compliance</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Secure', description: 'Enterprise-ready security controls and encryption' },
              { icon: Zap, title: 'Scalable', description: 'Handle millions of operations seamlessly' },
              { icon: CheckCircle2, title: 'Reliable', description: '99.9% uptime with 24/7 monitoring' }
            ].map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl border border-slate-200 p-8 text-center"
                  data-testid={`benefit-card-${index}`}
                >
                  <div className="w-14 h-14 rounded-lg bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                    <ItemIcon className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" data-testid="pricing-section-heading">Pricing Plans</h2>
            <p className="text-lg text-slate-600">Choose the plan that fits your needs</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.tier}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-xl border-2 p-8 ${
                  tier.tier === 'professional'
                    ? 'border-indigo-600 bg-indigo-50/50'
                    : 'border-slate-200 bg-white'
                }`}
                data-testid={`pricing-tier-${tier.tier}`}
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900 capitalize mb-2">{tier.tier}</h3>
                  <div className="text-2xl font-bold text-slate-900">
                    Custom pricing
                  </div>
                  <div className="text-sm text-slate-600 mt-1">Contact Digian.ai Sales to discuss this plan.</div>
                </div>
                <div className="space-y-3 mb-8">
                  {Object.entries(tier)
                    .filter(([key]) => key !== 'tier' && key !== 'price')
                    .map(([key, value]) => (
                      <div key={key} className="flex items-center space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                        <span className="text-sm text-slate-700 capitalize">
                          {value} {key}
                        </span>
                      </div>
                    ))}
                </div>
                <Link to="/demo">
                  <Button
                    className="w-full"
                    variant={tier.tier === 'professional' ? 'default' : 'outline'}
                    data-testid={`pricing-cta-${tier.tier}`}
                  >
                    {tier.tier === 'enterprise' ? 'Contact Sales' : 'Get Started'}
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-bold text-white" data-testid="product-cta-heading">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-indigo-100">
              Experience {product.name} with a personalized demo
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo" data-testid="product-cta-demo-btn">
                <Button size="lg" variant="secondary">
                  Request Demo
                </Button>
              </Link>
              <Link to="/contact" data-testid="product-cta-contact-btn">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-indigo-600"
                >
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetailPage;